import React, { useState, useEffect } from 'react';
import { Sparkles, Gift, Loader2, Plus, Trash2, AlertTriangle, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const TedsWheelAdmin = () => {
  const [data, setData] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/admin/teds-wheel`, { headers: authHeaders() });
      setData(res.data);
      setDirty(false);
    } catch {
      toast.error('Could not load Ted\u2019s Wheel');
    }
  };

  useEffect(() => { fetchData(); }, []);

  if (!data) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 text-gray-300 animate-spin" />
      </div>
    );
  }

  const patch = (updater) => {
    setData(prev => ({ ...prev, ...updater(prev) }));
    setDirty(true);
  };

  const updatePrize = (idx, key, value) => {
    patch(prev => ({
      prizes: prev.prizes.map((p, i) => (i === idx ? { ...p, [key]: value } : p)),
    }));
  };

  const addPrize = () => patch(prev => ({
    prizes: [
      ...prev.prizes,
      { id: '', label: 'New prize', code: '', description: '', weight: 1, claim_at: 'https://ultimatepetfoods.co.uk' },
    ],
  }));

  const removePrize = (idx) => {
    if (!window.confirm('Remove this prize?')) return;
    patch(prev => ({ prizes: prev.prizes.filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/admin/teds-wheel`,
        { enabled: data.enabled, prizes: data.prizes.map(({ id, label, code, description, weight, claim_at }) => ({ id, label, code, description, weight, claim_at })) },
        { headers: authHeaders() },
      );
      setData(prev => ({ ...res.data, stats: prev.stats }));
      setDirty(false);
      toast.success('Ted\u2019s Wheel updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const resetSpins = async () => {
    if (!window.confirm('Reset EVERY customer\u2019s spin so they can spin again? This cannot be undone.')) return;
    setResetting(true);
    try {
      const res = await axios.post(`${API}/admin/teds-wheel/reset-spins`, {}, { headers: authHeaders() });
      toast.success(`Reset ${res.data.reset_count} spins`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    } finally {
      setResetting(false);
    }
  };

  const totalSpins = data.stats?.total_spins || 0;

  return (
    <div className="space-y-5" data-testid="teds-wheel-admin">
      <div className="bg-gradient-to-r from-[#1f6d62] to-[#0FA49C] rounded-2xl p-5 text-white shadow">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-white/70 font-bold flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> Ted&apos;s Wheel
            </p>
            <h2 className="text-2xl font-black">Welcome spin-to-win</h2>
            <p className="text-sm text-white/80 mt-1">One free spin per registered customer, shown on their account page.</p>
          </div>
          <label className="flex items-center gap-2">
            <span className="text-xs text-white/70 font-bold uppercase tracking-widest">{data.enabled ? 'Live' : 'Off'}</span>
            <button
              type="button"
              onClick={() => patch(prev => ({ enabled: !prev.enabled }))}
              className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${
                data.enabled ? 'bg-[#a0da39]' : 'bg-white/25'
              }`}
              data-testid="teds-wheel-toggle"
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform shadow ${
                data.enabled ? 'translate-x-8' : 'translate-x-1'
              }`} />
            </button>
          </label>
        </div>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
          <div className="bg-white/10 rounded-lg p-2.5">
            <p className="text-[10px] uppercase tracking-widest text-white/70 font-bold">Total spins</p>
            <p className="text-2xl font-black mt-0.5" data-testid="teds-wheel-total-spins">{totalSpins}</p>
          </div>
          {(data.stats?.by_prize || []).slice(0, 3).map(b => (
            <div key={b.prize_id} className="bg-white/10 rounded-lg p-2.5">
              <p className="text-[10px] uppercase tracking-widest text-white/70 font-bold truncate">{b.label}</p>
              <p className="text-2xl font-black mt-0.5">{b.count}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#666] font-bold">Prizes</p>
            <p className="text-xs text-gray-400 mt-0.5">Weight sets the odds — higher = shown more often.</p>
          </div>
          <Button size="sm" variant="outline" onClick={addPrize} data-testid="teds-wheel-add-prize">
            <Plus className="w-4 h-4 mr-1" /> Add prize
          </Button>
        </div>

        <div className="space-y-2">
          {data.prizes.map((p, i) => (
            <div key={p.id || i} className="border border-gray-200 rounded-lg p-3" data-testid={`teds-wheel-prize-row-${i}`}>
              <div className="grid grid-cols-12 gap-2 items-center">
                <div className="col-span-12 sm:col-span-3">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-0.5 block">Label</label>
                  <Input
                    value={p.label || ''}
                    onChange={(e) => updatePrize(i, 'label', e.target.value)}
                    placeholder="10% OFF"
                    data-testid={`teds-wheel-prize-label-${i}`}
                  />
                </div>
                <div className="col-span-6 sm:col-span-2">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-0.5 block">Code</label>
                  <Input
                    value={p.code || ''}
                    onChange={(e) => updatePrize(i, 'code', e.target.value.toUpperCase())}
                    placeholder="SPIN10"
                    className="font-mono uppercase"
                    data-testid={`teds-wheel-prize-code-${i}`}
                  />
                </div>
                <div className="col-span-6 sm:col-span-1">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-0.5 block">Weight</label>
                  <Input
                    type="number"
                    min="1"
                    max="100"
                    value={p.weight ?? 1}
                    onChange={(e) => updatePrize(i, 'weight', Math.max(1, parseInt(e.target.value || '1', 10)))}
                    data-testid={`teds-wheel-prize-weight-${i}`}
                  />
                </div>
                <div className="col-span-11 sm:col-span-5">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-0.5 block">Description</label>
                  <Input
                    value={p.description || ''}
                    onChange={(e) => updatePrize(i, 'description', e.target.value)}
                    placeholder="10% off your next order"
                    data-testid={`teds-wheel-prize-desc-${i}`}
                  />
                </div>
                <div className="col-span-1 flex justify-end">
                  <button
                    type="button"
                    onClick={() => removePrize(i)}
                    className="text-gray-400 hover:text-red-500 p-1.5"
                    data-testid={`teds-wheel-prize-delete-${i}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="col-span-12">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-0.5 block">
                    Claim at <span className="text-gray-400 normal-case tracking-normal font-normal">(URL for online-only, or a location note like &ldquo;In-store&rdquo;)</span>
                  </label>
                  <Input
                    value={p.claim_at || ''}
                    onChange={(e) => updatePrize(i, 'claim_at', e.target.value)}
                    placeholder="https://ultimatepetfoods.co.uk"
                    data-testid={`teds-wheel-prize-claimat-${i}`}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {dirty && (
          <div className="mt-4 flex items-center gap-2 justify-end">
            <Button variant="outline" onClick={fetchData} disabled={saving}>Discard</Button>
            <Button onClick={save} disabled={saving} className="bg-[#0FA49C] hover:bg-[#0d8f88] text-white" data-testid="teds-wheel-save">
              {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</> : 'Save changes'}
            </Button>
          </div>
        )}
      </div>

      <div className="bg-red-50 border border-red-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-bold text-red-700">Reset all spins</p>
            <p className="text-xs text-red-600 mt-0.5">Wipe every customer&apos;s recorded spin so they can spin again. Use when running a new campaign.</p>
          </div>
          <Button size="sm" variant="outline" onClick={resetSpins} disabled={resetting} className="border-red-300 text-red-600 hover:bg-red-100" data-testid="teds-wheel-reset">
            {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4 mr-1.5" /> Reset spins</>}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TedsWheelAdmin;
